import { Box, Button, Text } from '@chakra-ui/react'
import { useState } from 'react'

import { useAuthContext } from 'hooks/useUserContext'
import { useViewAs } from 'hooks/useViewAs'

const BANNER_HEIGHT = '40px'
const BANNER_TOTAL_HEIGHT = `calc(${BANNER_HEIGHT} + env(safe-area-inset-bottom, 0px))`

/**
 * Fixed bottom bar shown for the whole read-only view-as session. Mounted once
 * at the root route so it appears on every page the user can reach. The top is
 * the sticky NavBar, so the bar sits at the bottom, clears the persistent
 * sidebar (`--bc-sidebar-offset`, set by BaseLayout) and respects the bottom
 * safe area. An in-flow spacer of equal height keeps the last content reachable.
 */
export const ViewAsBanner = () => {
  const viewAs = useViewAs()
  const { currentUser, exitViewAs } = useAuthContext()
  const [pending, setPending] = useState(false)

  if (!viewAs || !currentUser) return null

  const email = Array.isArray(currentUser.email)
    ? currentUser.email[0]
    : currentUser.email
  const name = [currentUser.nameFirst, currentUser.nameLast]
    .filter(Boolean)
    .join(' ')

  const onExit = async () => {
    setPending(true)
    try {
      await exitViewAs()
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <Box
        h={BANNER_TOTAL_HEIGHT}
        css={{ '@media print': { display: 'none' } }}
      />
      <Box
        role='status'
        position='fixed'
        bottom={0}
        left={0}
        right={0}
        ml={{ xl: 'var(--bc-sidebar-offset, 0px)' }}
        h={BANNER_TOTAL_HEIGHT}
        pb='env(safe-area-inset-bottom, 0px)'
        px={3}
        display='flex'
        alignItems='center'
        gap={3}
        bg='orange.600'
        color='white'
        zIndex={1250}
        transition='margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        css={{ '@media print': { display: 'none' } }}
      >
        <Text fontWeight='semibold' truncate minW={0}>
          {`Viewing as ${name} (${email}). Read-only.`}
        </Text>
        <Button
          size='xs'
          variant='outline'
          color='white'
          flexShrink={0}
          disabled={pending}
          onClick={onExit}
        >
          Exit
        </Button>
      </Box>
    </>
  )
}
